import React, { useState, useEffect } from 'react';
import { org } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trophy, Users, Euro, TrendingUp, BarChart3, PieChart } from 'lucide-react';
import { GAMES, getGameColor, getGameName } from '../constants/theme';

export default function OrgStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    org.getStats().then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div></div>;

  const statCards = [
    { title: 'Tournois créés', value: stats?.total_tournaments || 0, icon: Trophy, color: '#FF6B35' },
    { title: 'Total inscrits', value: stats?.total_participants || 0, icon: Users, color: '#4ECDC4' },
    { title: 'Revenu total', value: `${(stats?.total_revenue || 0).toFixed(0)}€`, icon: Euro, color: '#7B68EE' },
    { title: 'Taux de remplissage', value: `${stats?.avg_fill_rate || 0}%`, icon: TrendingUp, color: '#FFCB05' },
  ];

  const monthlyData = Object.entries(stats?.by_month || {}).sort(([a], [b]) => a.localeCompare(b)).slice(-6);

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold text-white" data-testid="stats-title">Statistiques</h1><p className="text-[#666] mt-1">Vue d'ensemble de votre activité</p></div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <Card key={i} className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#666]">{s.title}</p><p className="text-3xl font-bold text-white mt-1">{s.value}</p></div>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}20` }}><s.icon className="w-6 h-6" style={{ color: s.color }} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><PieChart className="w-5 h-5 text-[#FF6B35]" />Tournois par jeu</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.by_game || {}).map(([game, count]) => {
                const maxCount = Math.max(...Object.values(stats?.by_game || { a: 1 }));
                return (
                  <div key={game} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: getGameColor(game) }} /><span className="text-[#B0B0B0]">{getGameName(game)}</span></div>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-[#252525] rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: getGameColor(game) }} /></div>
                  </div>
                );
              })}
              {Object.keys(stats?.by_game || {}).length === 0 && <p className="text-[#666] text-center py-4">Aucune donnée</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#4ECDC4]" />Par statut</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.by_status || {}).map(([status, count]) => {
                const statusMap = { upcoming: ['À venir', '#3B82F6'], in_progress: ['En cours', '#22C55E'], completed: ['Terminé', '#6B7280'], cancelled: ['Annulé', '#EF4444'] };
                const [label, color] = statusMap[status] || [status, '#666'];
                const maxCount = Math.max(...Object.values(stats?.by_status || { a: 1 }));
                return (
                  <div key={status} className="space-y-1">
                    <div className="flex items-center justify-between text-sm"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} /><span className="text-[#B0B0B0]">{label}</span></div><span className="text-white font-medium">{count}</span></div>
                    <div className="h-2 bg-[#252525] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: color }} /></div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {monthlyData.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#7B68EE]" />Évolution mensuelle</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-[#333]">
                  <th className="text-left p-3 text-[#666] font-medium">Mois</th>
                  <th className="text-right p-3 text-[#666] font-medium">Tournois</th>
                  <th className="text-right p-3 text-[#666] font-medium">Inscrits</th>
                  <th className="text-right p-3 text-[#666] font-medium">Revenu</th>
                </tr></thead>
                <tbody>
                  {monthlyData.map(([month, data]) => (
                    <tr key={month} className="border-b border-[#333]">
                      <td className="p-3 text-white">{month}</td>
                      <td className="p-3 text-right text-[#B0B0B0]">{data.tournaments}</td>
                      <td className="p-3 text-right text-[#B0B0B0]">{data.participants}</td>
                      <td className="p-3 text-right text-[#FF6B35] font-medium">{data.revenue.toFixed(0)}€</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
